<template>
  <TContainer class="mt-8 md:mt-12 relative">
    <TOverlay :show="isCreating || isGettingPrices" class="z-20" />

    <div class="clear-both">
      <div class="float-left w-full lg:w-2/3 lg:pr-2">
        <div class="">
          <ApiResponseError
            v-if="hasError"
            :api-response-error="formError"
            class="mb-8"
          />

          <template v-if="!createSuccess">
            <div v-if="cottageContactWhatsAppPage">
              <TTitle tag="h1" size="2xl" classes="mb-2">
                {{ t('views.cottage-contact-whatsapp.title') }}
              </TTitle>
              <TText
                v-if="
                  cottageContactWhatsAppPage.owner.answer &&
                  cottageContactWhatsAppPage.owner.answer.rate !== null
                "
                classes="mb-2"
              >
                {{
                  cottageContactWhatsAppPage.owner.answer.rate > 0
                    ? t('views.cottage-contact-whatsapp.answerRate', {
                        rate: cottageContactWhatsAppPage.owner.answer.rate,
                        range: t(
                          `range.${cottageContactWhatsAppPage.owner.answer.range}`,
                        ),
                        value: cottageContactWhatsAppPage.owner.answer.value,
                        unit: t(
                          `time.${cottageContactWhatsAppPage.owner.answer.unit}`,
                          cottageContactWhatsAppPage.owner.answer.value,
                        ),
                      })
                    : t('views.cottage-contact-whatsapp.noAnswerRate')
                }}
              </TText>
              <ULink
                class="text-sm"
                :active="true"
                :to="
                  routes().cottageDetail({
                    slug: cottageContactWhatsAppPage.slug,
                    province: cottageContactWhatsAppPage.province.slug,
                  })
                "
                >{{ t('views.cottage-contact-whatsapp.cottageLink') }}</ULink
              >
              <TSeparator class="mt-6 mb-8" />
            </div>
          </template>
        </div>
      </div>
      <div class="float-right w-full lg:w-1/3 lg:pl-2">
        <div class="">
          <template v-if="!createSuccess">
            <div v-if="cottageContactWhatsAppPage">
              <UCard :ui="publicCard">
                <TText variant="semibold" classes="mb-2">
                  {{ t('views.cottage-contact-whatsapp.price.message') }}
                </TText>
                <CottageContactWhatsAppPrice
                  :item="cottageContactWhatsAppPage"
                  @submit="onPriceSubmit"
                  @update-dates="onPriceUpdateDates"
                  @update-people="onPriceUpdatePeople"
                />

                <CottageContactWhatsAppPriceResponse
                  v-if="pricesResponse"
                  :item="pricesResponse"
                />
              </UCard>
            </div>
          </template>
        </div>
      </div>
      <div class="float-left w-full lg:w-2/3 lg:pr-2 mb-20">
        <div class="">
          <template v-if="!createSuccess">
            <div v-if="cottageContactWhatsAppPage">
              <CottageContactWhatsAppForm
                :item="cottageContactWhatsAppPage"
                @submit="onSubmit"
              />
              <TText size="sm" color="text-gray-600" classes="mt-4">
                {{ t('views.cottage-contact-whatsapp.underFormMessage') }}
              </TText>
            </div>
          </template>

          <CottageContactWhatsAppPageSuccess
            v-if="
              createSuccess && cottageContactWhatsAppPage && onSuccessFormData
            "
            :id="cottageContactWhatsAppPage.id"
            :name="cottageContactWhatsAppPage.name"
            :form-data="onSuccessFormData"
          />
        </div>
      </div>
    </div>
  </TContainer>
</template>

<script setup lang="ts">
// Composables
import { useCottageContactWhatsAppPage } from '../composables/useCottageContactWhatsAppPage'
import { useCottageContactWhatsAppPagePrices } from '../composables/useCottageContactWhatsAppPagePrices'
import {
  sendGa4ContactEvent,
  sendCottageConversionAll,
} from '~~/modules/integrations/ga4/composables/useAnalyticsGa4Composable'
import { sendAdsContact } from '~~/modules/integrations/ads/composables/useAnalyticsAdsComposable'
import {
  sendConnectifPurchase,
  sendConnectifPageProductCart,
} from '~~/modules/integrations/connectif/composables/useConnectifComposable'

// Entities
import type { CottageContactWhatsAppPage } from '../entities/CottageContactWhatsAppPage'
import type {
  CottageContactWhatsAppPageCreateForm,
  CottageContactWhatsAppPageCreateResponse,
} from '../entities/CottageContactWhatsAppPageCreate'
import type {
  CottageContactWhatsAppPagePricesForm,
  CottageContactWhatsAppPagePricesDates,
  CottageContactWhatsAppPagePricesPeople,
  CottageContactWhatsAppPagePricesResponse,
} from '../entities/CottageContactWhatsAppPagePrices'

// Constants
import { CONNECTIF_DEFAULT_PRICE } from '~~/modules/integrations/connectif/constants/index'

// Design System
import { publicCard } from '~~/modules/design-system/utils/nuxt-ui-config/components/card'

// Composables
const { sendFormData } = useCottageContactWhatsAppPage()
const { getPricesData } = useCottageContactWhatsAppPagePrices()
const { setCookieRuralityCottageContact } = useCookiesRurality()

// Component state
const isCreating = ref(false)
const isGettingPrices = ref(false)
const hasError = ref(false)
const createSuccess = ref(false)

// Props
interface propsInterface {
  cottageContactWhatsAppPage: CottageContactWhatsAppPage
}

const props = defineProps<propsInterface>()

// Cart ID ref
const ConnectifCartId: Ref<string | null> = ref(null)
const ConnectifTotalPrice: Ref<number> = ref(CONNECTIF_DEFAULT_PRICE.CONTACT)

// Constants
let formError: ApiResponseError | null = null
const dates = ref<CottageContactWhatsAppPagePricesDates | null>(null)
const people = ref<CottageContactWhatsAppPagePricesPeople | null>(null)

const onSuccessFormData = ref<CottageContactWhatsAppPageCreateForm | null>(null)

const pricesResponse = ref<CottageContactWhatsAppPagePricesResponse | null>(
  null,
)

// Methods
const onPriceSubmit = async (ev: CottageContactWhatsAppPagePricesForm) => {
  isGettingPrices.value = true
  if (props.cottageContactWhatsAppPage) {
    const responseGetPricesData = await getPricesData({
      CottageContactWhatsAppPage: props.cottageContactWhatsAppPage,
      dates: ev.dates,
      people: ev.people,
    })
    pricesResponse.value = responseGetPricesData

    if (pricesResponse.value && pricesResponse.value.value) {
      ConnectifTotalPrice.value = pricesResponse.value.value
    }
  }
  isGettingPrices.value = false
}

const onPriceUpdateDates = async (
  ev: CottageContactWhatsAppPagePricesDates,
) => {
  dates.value = ev
}

const onPriceUpdatePeople = async (
  ev: CottageContactWhatsAppPagePricesPeople,
) => {
  people.value = ev
}

const onSubmit = async (ev: CottageContactWhatsAppPageCreateForm) => {
  isCreating.value = true
  hasError.value = false
  if (props.cottageContactWhatsAppPage) {
    const responseSendFormData = await sendFormData({
      createForm: ev,
      CottageContactWhatsAppPage: props.cottageContactWhatsAppPage,
      dates: dates.value,
      people: people.value,
    })
    if (responseSendFormData.apiResponseError) {
      formError = responseSendFormData.apiResponseError
      hasError.value = true
      isCreating.value = false
    } else if (responseSendFormData.apiResponseSuccess) {
      onSuccess(responseSendFormData.apiResponseSuccess, ev)
    }
  }
}

const onSuccess = (
  CottageContactWhatsAppPageCreateResponse: CottageContactWhatsAppPageCreateResponse,
  formData: CottageContactWhatsAppPageCreateForm,
) => {
  if (props.cottageContactWhatsAppPage) {
    setDataRepository(formData)
    onSuccessFormData.value = formData

    sendGa4ContactEvent({
      province: props.cottageContactWhatsAppPage.province.name,
      method: 'whatsapp',
    })
    sendCottageConversionAll()
    if (CottageContactWhatsAppPageCreateResponse) {
      sendAdsContact({
        email: CottageContactWhatsAppPageCreateResponse.traveler.email,
        phone: CottageContactWhatsAppPageCreateResponse.traveler.phone,
      })
    }

    // Connectif
    if (
      ConnectifCartId.value &&
      CottageContactWhatsAppPageCreateResponse.traveler.email
    ) {
      sendConnectifPurchase(
        {
          ...props.cottageContactWhatsAppPage,
          cartId: ConnectifCartId.value,
          productPrice: ConnectifTotalPrice.value,
          paymentMethod: 'Contact',
        },
        {
          entityInfo: {
            primaryKey: CottageContactWhatsAppPageCreateResponse.traveler.email,
          },
        },
      )
      history.replaceState(null, '', '#success')
    }

    isCreating.value = false
    createSuccess.value = true
  }
}

const setDataRepository = (formData: CottageContactWhatsAppPageCreateForm) => {
  setCookieRuralityCottageContact({
    message: formData.message,
    traveler_email: formData.email,
    traveler_name: formData.name,
    traveler_phone: formData.phone,
  })
}

// Initialize cart and send tracking events
onMounted(() => {
  if (props.cottageContactWhatsAppPage) {
    ConnectifCartId.value = new Date().getTime().toString()

    sendConnectifPageProductCart({
      ...props.cottageContactWhatsAppPage,
      cartId: ConnectifCartId.value,
      productPrice: ConnectifTotalPrice.value,
      paymentMethod: 'Contact',
    })
  }
})
</script>
